import { LuneTheme } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import { FC, ReactNode, useEffect, useState } from 'react'

import { spaceGrotesk } from '@/app/fonts'
import useDevice from '@/hooks/useDevice'

const { palette } = LuneTheme

const ColoredChip: FC<{
    children: ReactNode
    version: 'light-green' | 'dark-green'
    loadingEffect?: boolean
    animationDelay?: string
}> = ({ children, version, loadingEffect, animationDelay }) => {
    const animated = version === 'light-green'
    const bgColor = animated ? '#E1F1E6' : '#048327'
    const { smScreen } = useDevice()

    const [showLoadingEffect, setShowLoadingEffect] = useState(loadingEffect)

    useEffect(() => {
        if (showLoadingEffect) {
            setTimeout(() => {
                setShowLoadingEffect(false)
            }, 1500)
        }
    }, [showLoadingEffect])

    return (
        <Box
            sx={{
                color: version === 'dark-green' ? palette.White : palette.Grey900,
                height: '24px',
                lineHeight: '24px',
                display: 'inline-block',
                borderRadius: '4px',
                pl: 1 / 2,
                pr: 1 / 2,
                position: 'relative',
                overflow: 'hidden',
                fontFamily: `${spaceGrotesk.style.fontFamily}`,
                fontWeight: '500',
                fontSize: '16px',
                verticalAlign: 'middle',
                zIndex: 1,
                width: 'max-content',

                '&:after': {
                    content: '""',
                    position: 'absolute',
                    backgroundColor: showLoadingEffect ? 'transparent' : bgColor,
                    top: 0,
                    left: 0,
                    width: animated ? '0' : '100%',
                    height: '24px',
                    zIndex: -1,

                    ...(animated && {
                        '@keyframes animated-width': {
                            '0%': {
                                width: '0',
                            },
                            '100%': {
                                width: '100%',
                            },
                        },
                        animation: `animated-width 0.3s linear ${loadingEffect ? '0s' : animationDelay || '1s'} forwards`,
                    }),
                },

                ...(loadingEffect && {
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '24px',
                        background: 'linear-gradient(to right, #fff 0%, #E1F1E6 45%, #fff 95%)',
                        backgroundSize: '200px 100%',
                        animation: 'shimmer 2s infinite',
                        opacity: showLoadingEffect ? 1 : 0,
                        transition: 'opacity .3s ease-in-out',
                        '@keyframes shimmer': {
                            from: {
                                backgroundPosition: '-100px 0',
                            },
                            to: {
                                backgroundPosition: '100px 0',
                            },
                        },
                    },
                }),

                ...(smScreen && {
                    '.MuiTypography-root': {
                        fontSize: '8px !important',
                        lineHeight: '12px !important',
                    },
                    fontSize: '8px !important',
                    lineHeight: '12px !important',
                    height: '12px',
                }),
            }}
        >
            {children}
        </Box>
    )
}

export default ColoredChip
